import React, {useEffect, useMemo, useState} from 'react';
import {useAppDispatch, useAppSelector} from "../../../../redux/hooks";
import {InputSwitch} from "primereact/inputswitch";
import {Button} from "primereact/button";
import {InputNumber} from "primereact/inputnumber";
import {setTrunkField, setTrunkIds} from "../../../../redux/actions/actionsTrunk";
import {ConfirmDialog} from "primereact/confirmdialog";
import {archiveTrunkPhoneById, inactiveTrunkPhoneById} from "../../../../redux/api/apiTrunks";
import {Divider} from "primereact/divider";
import {classNames} from "primereact/utils";
import {InputText} from "primereact/inputtext";
import {checkPermissions} from "../../../../redux/permissions/permissionsUtils";
import {TabPanel, TabView} from "primereact/tabview";

const isPhoneArchived = (phone: { isArchived?: boolean }) => phone?.isArchived === true;

type TrunkPhoneEntry = { phone: any; index: number };

const PhoneSettings = () => {
    const dispatch = useAppDispatch();
    const rolesVision = useAppSelector(
        (state) => state.RolesReducer.rolesVision
    ) as unknown as Record<string, string[]>;
    const {create: hasTrunksCreatePermission} = checkPermissions(rolesVision, "Trunks");
    const trunkData = useAppSelector(state => state.TrunksListReducer.trunkData);
    const errorFields = useAppSelector(state => state.TrunksListReducer.errorFields);
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const [selectedPairs, setSelectedPairs] = useState<any>([{phoneNo: null, isActive: false, isArchived: false}]);
    const archiveTableToggle = useAppSelector(state => state.TrunksListReducer.archiveTrunkTableToggle)
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [visible, setVisible] = useState<boolean>(false);
    const [visibleInActive, setVisibleInActive] = useState<boolean>(false);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const [currentId, setCurrentId] = useState<any>(null);
    const [inactiveId, setInactiveId] = useState<any>(null);
    const [phoneSubTabIndex, setPhoneSubTabIndex] = useState(0);

    const syncPhonesToStore = (pairs: any[]) => {
        const filtered = pairs.filter(
            (item) => item?.phoneNo != null && item.phoneNo !== ""
        );
        dispatch(setTrunkIds(filtered.length > 0 ? filtered : null));
    };

    useEffect(() => {
        if (!trunkData || !Array.isArray(trunkData.phones)) {
            return;
        }
        const phones = trunkData.phones;
        setSelectedPairs(
            phones.length > 0 ? phones : [{phoneNo: null, isActive: false, isArchived: false}]
        );
        if (phones.length > 0) {
            syncPhonesToStore(phones);
        }
    }, [trunkData]);

    const activePhoneEntries = useMemo(
        () =>
            selectedPairs
                .map((phone: any, index: number) => ({phone, index}))
                .filter(({phone}: TrunkPhoneEntry) => !isPhoneArchived(phone)),
        [selectedPairs],
    );

    const archivedPhoneEntries = useMemo(
        () =>
            selectedPairs
                .map((phone: any, index: number) => ({phone, index}))
                .filter(({phone}: TrunkPhoneEntry) => isPhoneArchived(phone)),
        [selectedPairs],
    );

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const removePair = (indexToRemove: number) => {
        setSelectedPairs((currentPairs: any[]) =>
            currentPairs.filter((_, index) => index !== indexToRemove)
        );
    };

    const deletePair = () => {
        if (!hasTrunksCreatePermission) {
            return;
        }
        dispatch(archiveTrunkPhoneById(trunkData?.id, currentId, jwtToken?.jwtToken));
        setVisible(false);
    }

    const addPair = () => {
        setSelectedPairs((prevPairs: any[]) => {
            const newPhone = {
                "phoneNo": null,
                "isActive": false,
                "isArchived": false,
            }
            return [...prevPairs, newPhone];
        });
    };

    useEffect(() => {
        syncPhonesToStore(selectedPairs);
    }, [selectedPairs, visible]);

    const changePhone = (value: any, index: number) => {
        if (value === '' || /^\d+$/.test(value)) {
            setSelectedPairs((prevPairs: any[]) =>
                prevPairs.map((pair, idx) =>
                    idx === index ? {...pair, phoneNo: value} : pair
                )
            );
        }
    };

    const changeActive = (id: number) => {
        setVisibleInActive(true);
        setInactiveId(id);
    };

    const inActivePhone = () => {
        dispatch(inactiveTrunkPhoneById(inactiveId, trunkData?.id, jwtToken?.jwtToken));
    }

    const activeNumber = (value: any, index: number) => {
        setSelectedPairs((prevPairs: any[]) =>
            prevPairs.map((pair, idx) =>
                idx === index ? {...pair, isActive: value} : pair
            )
        );
    }

    const phonesErrorMessage =
        errorFields?.phones &&
        typeof errorFields.phones === "object" && true &&
        ("phones" in errorFields.phones || "name" in errorFields.phones)
            ? (errorFields.phones as { phones?: string; name?: string }).phones ??
              (errorFields.phones as { phones?: string; name?: string }).name
            : undefined;

    return (
        <div className="grid p-fluid">
            <div className="field col-12 md:col-12 flex flex-wrap mb-0">
                <div className="flex col-12 md:col-3 justify-content-between align-items-center pb-0 pr-0">
                    <label>Ліміт спроб дозвону</label>
                    <InputNumber
                        useGrouping={false}
                        value={
                            trunkData?.limitAttemptsCall != null &&
                            trunkData.limitAttemptsCall !== ""
                                ? Number(trunkData.limitAttemptsCall)
                                : undefined
                        }
                        className={classNames('max-w-5rem', {'p-invalid': errorFields?.limitAttemptsCall})}
                        maxLength={3}
                        onChange={e => {
                            dispatch(setTrunkField({section: 'trunk', fieldName: 'limitAttemptsCall', value: e.value}));
                        }} disabled={!archiveTableToggle || !editType}
                    />
                </div>
                {errorFields?.limitAttemptsCall && <div className="col-12 md:col-12 pt-2 pb-0"><span className="title p-error text-sm">{errorFields?.limitAttemptsCall?.limitAttemptsCall}*</span></div>}
            </div>
            <Divider className="mx-2"/>
            <div className="field col-12 md:col-12 flex flex-wrap mb-0">
                <TabView
                    className="w-full"
                    activeIndex={phoneSubTabIndex}
                    onTabChange={(e) => setPhoneSubTabIndex(e.index)}
                >
                    <TabPanel header="Номери">
                        <div className="field col-6 md:col-4 mb-0">
                            <label>Номер*</label>
                        </div>
                        <div className="field col-6 md:col-3 mb-0">
                            <label>Стан*</label>
                        </div>
                        <div className="field col-12 md:col-12 flex flex-wrap align-items-center">
                            {activePhoneEntries.map(({phone, index}: TrunkPhoneEntry) => (
                                <div
                                    key={phone.id != null ? `id-${phone.id}` : `idx-${index}`}
                                    className="flex flex-wrap field col-12 p-0 m-0 align-items-center"
                                >
                                    <div className="field col-6 md:col-4 p-0 m-0">
                                        <InputText
                                            onChange={(e) => changePhone(e.target.value, index)}
                                            onKeyPress={(e) => {
                                                const allowedKeys = /[0-9]|Backspace|Tab|Delete|ArrowLeft|ArrowRight/;
                                                if (!allowedKeys.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            disabled={!archiveTableToggle || !editType}
                                            className={classNames("max-w-20rem", {
                                                "p-invalid":
                                                    errorFields?.phoneRowErrors?.[index] != null &&
                                                    errorFields.phoneRowErrors[index] !== "",
                                            })}
                                            value={phone.phoneNo ?? ""}
                                        />
                                    </div>
                                    <div className="field col-6 md:col-3 flex align-items-center mb-0">
                                        <InputSwitch
                                            disabled={!archiveTableToggle || !editType}
                                            className="mr-3 ml-1"
                                            checked={phone.isActive}
                                            onChange={(e) => {
                                                if (phone.isActive) {
                                                    changeActive(phone.id);
                                                } else {
                                                    activeNumber(e.target.value, index);
                                                }
                                            }}
                                        />
                                        {windowWidth > 1248 && (
                                            <label className="mb-0" htmlFor="icon">
                                                {phone.isActive ? "Активний" : "Не активний"}
                                            </label>
                                        )}
                                        <Button
                                            icon="pi pi-times"
                                            className="p-button-secondary ml-auto"
                                            disabled={
                                                !archiveTableToggle ||
                                                !editType ||
                                                selectedPairs.length < 1 ||
                                                (!!phone.id && !hasTrunksCreatePermission)
                                            }
                                            onClick={() => {
                                                if (!phone.id) {
                                                    removePair(index);
                                                } else {
                                                    if (!hasTrunksCreatePermission) {
                                                        return;
                                                    }
                                                    setCurrentId(phone?.id);
                                                    setVisible(true);
                                                }
                                            }}
                                        />
                                    </div>
                                    {errorFields?.phoneRowErrors?.[index] ? (
                                        <div className="field col-12 p-0 m-0 pb-2">
                                            <span className="title p-error text-sm">
                                                {errorFields.phoneRowErrors[index]}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                            {phonesErrorMessage ? (
                                <div className="col-12 md:col-12 pt-2 px-0 pb-0">
                                    <span className="title p-error text-sm">{phonesErrorMessage}*</span>
                                </div>
                            ) : null}
                            <Button
                                disabled={!archiveTableToggle || !editType}
                                icon="pi pi-plus"
                                onClick={() => addPair()}
                                className="max-w-12rem mt-4"
                            >
                                <label className="pl-2">Додати номер</label>
                            </Button>
                        </div>
                    </TabPanel>
                    <TabPanel header="Архів">
                        <div className="field col-6 md:col-4 mb-0">
                            <label>Номер*</label>
                        </div>
                        <div className="field col-6 md:col-3 mb-0">
                            <label>Стан*</label>
                        </div>
                        <div className="field col-12 md:col-12 flex flex-wrap align-items-center">
                            {archivedPhoneEntries.length === 0 ? (
                                <div className="col-12 py-3 text-color-secondary">
                                    Немає архівних номерів
                                </div>
                            ) : (
                                archivedPhoneEntries.map(({phone, index}: TrunkPhoneEntry) => (
                                    <div
                                        key={phone.id != null ? `arch-id-${phone.id}` : `arch-idx-${index}`}
                                        className="flex flex-wrap field col-12 p-0 m-0 align-items-center"
                                    >
                                        <div className="field col-6 md:col-4 p-0 m-0">
                                            <InputText
                                                disabled
                                                className="max-w-20rem"
                                                value={phone.phoneNo ?? ""}
                                            />
                                        </div>
                                        <div className="field col-6 md:col-3 flex align-items-center mb-0">
                                            <InputSwitch
                                                disabled
                                                className="mr-3 ml-1"
                                                checked={phone.isActive}
                                            />
                                            {windowWidth > 1248 && (
                                                <label className="mb-0" htmlFor="icon">
                                                    {phone.isActive ? "Активний" : "Не активний"}
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </TabPanel>
                </TabView>
            </div>
            <ConfirmDialog visible={visible} onHide={() => setVisible(false)} message={'Хочете видалити номер ?'}
                           icon="pi pi-exclamation-triangle" acceptLabel="Продовжити" rejectLabel="Відмінити"
                           accept={() => deletePair()}
            />
            <ConfirmDialog visible={visibleInActive} onHide={() => setVisibleInActive(false)} message={'Хочете деактивувати номер ?'}
                           icon="pi pi-exclamation-triangle" acceptLabel="Продовжити" rejectLabel="Відмінити"
                           accept={() => inActivePhone()}
            />
        </div>
    );
};

export default PhoneSettings;
